// Always-dark backdrop of the landing hero: the live atlas islands when the
// atlas is built, a canvas constellation otherwise. Decorative (aria-hidden);
// the only interactive part is the "Open the atlas" link the page renders.
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { AtlasCore } from '../atlas/atlasShared';
import { startConstellation } from './constellation';

type Mode = 'loading' | 'atlas' | 'fallback';

export default function HeroStage({
  core,
  coreState,
}: {
  core: AtlasCore | null | undefined;
  coreState: 'loading' | 'ready' | 'missing' | 'error';
}) {
  const stageRef = useRef<HTMLDivElement>(null);
  const hostRef = useRef<HTMLDivElement>(null);
  const underlayRef = useRef<HTMLCanvasElement>(null);
  const labelsRef = useRef<HTMLDivElement>(null);
  const fallbackRef = useRef<HTMLCanvasElement>(null);
  const [mode, setMode] = useState<Mode>('loading');

  // Atlas: lazy-load the sigma scene once core.json is in.
  useEffect(() => {
    if (coreState === 'missing' || coreState === 'error') {
      setMode('fallback');
      return;
    }
    if (coreState !== 'ready' || !core) return;
    let cancelled = false;
    let scene: { dispose: () => void } | null = null;
    import('./heroAtlas')
      .then(({ HeroAtlas }) => {
        if (cancelled) return;
        scene = new HeroAtlas(
          stageRef.current as HTMLDivElement,
          hostRef.current as HTMLDivElement,
          underlayRef.current as HTMLCanvasElement,
          labelsRef.current as HTMLDivElement,
          core,
        );
        setMode('atlas');
      })
      .catch((e) => {
        if (cancelled) return;
        if (import.meta.env.DEV) console.warn('[landing] hero atlas unavailable', e);
        setMode('fallback');
      });
    return () => {
      cancelled = true;
      scene?.dispose();
    };
  }, [core, coreState]);

  useEffect(() => {
    if (mode !== 'fallback' || !fallbackRef.current) return;
    return startConstellation(fallbackRef.current);
  }, [mode]);

  return (
    <div ref={stageRef} className={`landing-stage landing-stage-${mode}`} aria-hidden="true">
      <canvas ref={underlayRef} className="landing-stage-underlay" />
      <div ref={hostRef} className="landing-stage-map" />
      <div ref={labelsRef} className="landing-stage-labels" />
      {mode === 'fallback' && <canvas ref={fallbackRef} className="landing-stage-fallback" />}
      {/* Clicking the map opens it; keyboard users get the explicit link. */}
      <Link to="/overview" className="landing-stage-hit" tabIndex={-1} />
    </div>
  );
}
